import React, { useState, useCallback } from 'react';
import { Upload, message } from 'antd';
import { InboxOutlined, DeleteOutlined, FileImageOutlined } from '@ant-design/icons';
import CustomButton from '../../../../../../../../../v2-components/Antd/Button';
import './style.scss';

const { Dragger } = Upload;

const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function Step4PaymentProof({
  paymentProof,
  paymentProofPreview,
  onPaymentProofChange,
  onBack,
  onSave,
  isLoading,
}) {
  const [uploading, setUploading] = useState(false);

  const handleBeforeUpload = useCallback((file) => {
    // Validate file type
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      message.error('Solo se permiten imágenes (JPG, PNG, GIF, WebP) o PDF');
      return false;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      message.error('El archivo no puede superar los 10MB');
      return false;
    }

    return true;
  }, []);

  const handleUpload = useCallback(async (info) => {
    const { file, fileList } = info;
    
    if (file.status === 'uploading') {
      setUploading(true);
      return;
    }

    if (file.status === 'done' || file.status === 'error') {
      setUploading(false);
    }

    // Handle file selection (before actual upload)
    if (fileList.length > 0) {
      const selectedFile = fileList[fileList.length - 1];
      const originFile = selectedFile.originFileObj || selectedFile;
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        onPaymentProofChange(originFile, reader.result);
      };
      reader.readAsDataURL(originFile);
    }
  }, [onPaymentProofChange]);

  const handleRemoveProof = useCallback(() => {
    onPaymentProofChange(null, null);
  }, [onPaymentProofChange]);

  const customRequest = useCallback(({ onSuccess }) => {
    // We handle the file locally first, actual upload happens on save
    setTimeout(() => {
      onSuccess('ok');
    }, 100);
  }, []);

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: ACCEPTED_FILE_TYPES.join(','),
    showUploadList: false,
    beforeUpload: handleBeforeUpload,
    onChange: handleUpload,
    customRequest,
  };

  const isImage = paymentProof?.type?.startsWith('image/');
  const hasProof = !!paymentProof;
  const canSave = hasProof;

  return (
    <div className="step-payment-proof">
      <div className="step-header">
        <h2>Finalizar compra</h2>
        <p className="step-subtitle">Sube el comprobante de pago</p>
      </div>

      <div className="upload-section">
        {!hasProof ? (
          <Dragger {...uploadProps} className="upload-dragger">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Haz clic o arrastra el archivo aquí
            </p>
            <p className="ant-upload-hint">
              Soporta imágenes (JPG, PNG, GIF, WebP) y PDF. Máximo 10MB.
            </p>
          </Dragger>
        ) : (
          <div className="proof-preview-container">
            <div className="proof-preview">
              {isImage ? (
                <img 
                  src={paymentProofPreview} 
                  alt="Comprobante de pago" 
                  className="preview-image"
                />
              ) : (
                <div className="preview-file">
                  <FileImageOutlined className="file-icon" />
                  <span className="file-type">PDF</span>
                </div>
              )}
            </div>
            
            <div className="proof-info">
              <div className="proof-name">
                <FileImageOutlined />
                <span>{paymentProof.name}</span>
              </div>
              <CustomButton
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemoveProof}
                className="btn-remove"
              >
                Eliminar
              </CustomButton>
            </div>
          </div>
        )}
      </div>

      {uploading && (
        <div className="upload-progress">
          <span>Procesando archivo...</span>
        </div>
      )}

      <div className="step-footer">
        <CustomButton onClick={onBack} className="btn-back">
          Volver
        </CustomButton>
        <CustomButton
          type="primary"
          onClick={onSave}
          disabled={!canSave}
          loading={isLoading}
          className="btn-save"
        >
          Guardar
        </CustomButton>
      </div>
    </div>
  );
}
